import { useState, useEffect, RefObject, useRef } from 'react';
import { fetchStudioOverallOverviewTableData } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { reportOperationLog } from '@gp/services/global';
import { toFirstUpperCase } from '@gp/utils/common';

const DEFAULT_ORDER_STATE: IOrderState = {
  // order_metric: 'business_owner',
  // order: 'asc',
  order_metric: 'last_year_actual_gross_revenue',
  order: 'desc',
};

export default function useKeyInformationTable(options: {
  filterComp: RefObject<any>;
  isCommonDataLoading: boolean;
  commonData: IStudioOverallOverviewCommonData;
}) {
  const { filterComp, isCommonDataLoading, commonData } = options || {};
  const { categories, businesses } = commonData || {};
  const initialCategory = categories?.map((item) => item?.value) || [];
  const initialBusiness = businesses?.map((item) => item?.value) || [];
  // const [isLoading, setIsLoading] = useState(false);
  const [isTableInnerLoading, setIsTableInnerLoading] = useState(false);
  const [formModel, setFormModel] =
    useState<IStudioOverallOverviewKeyInformationTableFilterFormModel>({
      category: initialCategory,
      business: initialBusiness,
      regionType: 'market',
      regions: ['global'],
    });
  const [orderState, setOrderState] =
    useState<IOrderState>(DEFAULT_ORDER_STATE);
  const [records, setRecords] = useState<
    IStudioOverallOverviewKeyInformationTableRecord[]
  >([]);
  const currentParams = useRef<any>(null);
  const prevParams = useRef<any>(null);
  // 记录上一次请求是否有数据
  const [switchSavePrevParams, setSwitchSavePrevParams] = useState<boolean>(false);
  // 记录请求是否失败
  const [reqError, setReqError] = useState<boolean>(false);
  const [dataTime, setDataTime] = useState<string>('');
  // 保存参数下载需要用
  const [fetchParams, setFetchParams] = useState({});

  const categoryFormatter = (value: string): string => toFirstUpperCase(value);

  const businessFormatter = (value: string): string => toFirstUpperCase(value);

  const mapRecordFields = (
    data: any,
  ): IStudioOverallOverviewKeyInformationTableRecord => {
    return {
      ...data,
      id: data?.studio_id || '',
      name: data?.studio_name || '',
      cover: data?.studio_cover || '',
      addr: data?.country_name_en || data?.country_name_cn || '',
      nationalFlag: data?.national_flag || '',
      business: data?.business_owner || '',
      revenue: data?.last_year_actual_gross_revenue || '',
      revenue_budget: data?.this_year_budget_gross_revenue || '',
      fte: data?.full_time_employee || '',
      gamesInDev: data?.key_project_in_develop || [],
      gamesInLive: data?.key_project_in_live || [],
    };
  };

  const getRecords = async (
    payload?: IStudioOverallOverviewKeyInformationTableFetcherParams,
    isSearch?: boolean,
  ): Promise<IStudioOverallOverviewKeyInformationTableRecord[]> => {
    const params: IStudioOverallOverviewKeyInformationTableFetcherParams = {
      category: /* payload?.category ||  */[],
      business_owner: payload?.business_owner || [],
      country: payload?.country || [],
      order_metric: payload?.order_metric || '',
      // @ts-ignore
      order: payload?.order || '',
      studio_name: payload?.studio_name || '', // 用于点击搜索
      studio_id: payload?.studio_id || '', // 用于点击搜索
    };
    try {
      const res: any = await fetchStudioOverallOverviewTableData(params);
      const list = res?.list;
      setDataTime(res?.update_time || '');
      // 上一次请求有数据时
      if (currentParams.current && switchSavePrevParams) {
        prevParams.current = currentParams.current;
      }
      currentParams.current = {
        ...formModel,
        ...params,
        business: payload?.business_owner || [],
      };
      setSwitchSavePrevParams(Array.isArray(list) && list?.length > 0);
      setReqError(false);
      setFetchParams(params);
      // 搜索打点
      /* if (isSearch) {
        reportOperationLog({
          query: payload?.studio_name || '',
          extInfo: '500901', // 当前页面id
          clickId: payload?.studio_id || '',
          uidType: '',
          uid: '',
        }, 'search');
      } */
      const records: IStudioOverallOverviewKeyInformationTableRecord[] =
        list?.map(mapRecordFields);
      return records || [];
    } catch (exception) {
      currentParams.current = {...formModel, ...params};
      setReqError(true);
      setFetchParams({});
      return [];
    }
  };

  const handleFilterChange = (
    payload: IStudioOverallOverviewKeyInformationTableFilterFormModel,
  ) => {
    const country = filterComp?.current?.getRegionsForReq(
      payload?.regionType,
      payload?.regions,
    );
    const params = {
      category: payload?.category || [],
      business_owner: payload?.business || [],
      country: country || [],
      order_metric: orderState?.order_metric,
      order: orderState?.order,
    };
    // @ts-ignore
    query(params, setIsTableInnerLoading);
    setFormModel({ ...formModel, ...payload });
  };

  const handleOrderStateChange = (value: any) => {
    const newOrderState = {
      order_metric: value?.[0]?.by || '',
      order: value?.[0]?.order || '',
    };
    const country = filterComp?.current?.getRegionsForReq(
      formModel?.regionType,
      formModel?.regions,
    );
    const params = {
      category: formModel?.category || [],
      business_owner: formModel?.business || [],
      country: country || [],
      ...newOrderState,
    };
    query(params, setIsTableInnerLoading);
    setOrderState(newOrderState);
  };

  const handleSearchChange = (
    payload: IStudioOverallOverviewKeyInformationTableSearchParams,
  ) => {
    const country = filterComp?.current?.getRegionsForReq(
      payload?.regionType,
      payload?.regions,
    );
    const params = {
      category: payload?.category || [],
      business_owner: payload?.business || [],
      country: country || [],
      order_metric: orderState?.order_metric,
      order: orderState?.order,
      studio_name: payload?.studioName || '',
      studio_id: payload?.studioId || '',
    };
    // @ts-ignore
    query(params, setIsTableInnerLoading, true);
    setFormModel({ ...formModel, ...payload });
  };

  // 表格搜索空态及报错情况操作
  const handleTableStatusChange = (payload: any) => {
    // @ts-ignore
    query(payload, setIsTableInnerLoading);
    setFormModel({ ...payload });
  }

  const query = async (
    payload?: IStudioOverallOverviewKeyInformationTableFetcherParams,
    isLoadingSetter = setIsTableInnerLoading,
    isSearch = false,
  ) => {
    isLoadingSetter?.(true);
    setRecords([]);
    const records = await getRecords(payload, isSearch);
    setRecords(records);
    isLoadingSetter?.(false);
  };

  useEffect(() => {
    // if (isCommonDataLoading) return;
    const country = filterComp?.current?.getRegionsForReq(
      formModel?.regionType,
      formModel?.regions,
    );
    setFormModel({
      ...formModel,
      category: initialCategory,
      business: initialBusiness,
    });
    query({
      category: initialCategory,
      business_owner: initialBusiness,
      country: country || [],
      order_metric: orderState?.order_metric,
      // @ts-ignore
      order: orderState?.order,
    }, setIsTableInnerLoading);
  }, [/* isCommonDataLoading */]);

  return {
    // isLoading,
    isTableInnerLoading,
    formModel,
    orderState,
    categoryFormatter,
    businessFormatter,
    handleOrderStateChange,
    handleFilterChange,
    handleSearchChange,
    handleTableStatusChange,
    setRecords,
    records,
    currentParams,
    prevParams,
    reqError,
    dataTime,
    fetchParams,
  };
}
